import React from "react";
import { Button, ButtonColor, Container, ContainerSize, Gap, Heading, Modal, ModalFullscreen, ModalSize, Stack, StackItem, Text, Wrapper } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";
type ModalProps = {}

const ModalPage = ({ }: ModalProps) => {
    // Single state to manage all modals
    const [activeModal, setActiveModal] = React.useState<string | null>(null);

    // Generic handlers
    const openModal = (modalName: string) => setActiveModal(modalName);
    const closeModal = () => setActiveModal(null);

    // Check if specific modal is open
    const isModalOpen = (modalName: string) => activeModal === modalName;


    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Modal
            </Heading>
            <Text el="p">A <Text el="strong">Modal</Text> is a dialog-style component that appears on top of the current
                page to capture the
                user’s attention. It’s commonly used for actions like confirmations, forms, alerts, or displaying
                additional details without navigating away from the page.</Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Example
            </Heading>
            <Text className="mb-4">
                Click the button below to display a functional <Text el="strong">modal</Text> example. The <Text
                    el="strong">modal</Text> appears with a smooth slide and fade effect from the top of the screen.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3">
                        <Button color={ButtonColor.Primary} onClick={() => openModal('basic')} style={{ width: 'fit-content' }}>Launch demo modal</Button>
                    </Stack>
                    <Modal show={isModalOpen('basic')} onClose={closeModal}>
                        <Modal.Dialog >
                            <Modal.Header>
                                <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                <Button type="button" className="btn-close" onClick={closeModal}>&nbsp;</Button>
                            </Modal.Header>
                            <Modal.Body>
                                <Text el="p">This is the modal body content.</Text>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
                                <Button color={ButtonColor.Primary}>Save changes</Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ModalBasicCode}
                </CodeHighlighter>
                <CodeHighlighter language="tsx">
                    {ModalBasicCodeJs}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Static backdrop
            </Heading>
            <Text className="mb-4">
                When <Text el="strong">backdropClose</Text> set to <Text el="strong">false</Text>, the <Text
                    el="strong">modal</Text> will not close when clicking outside, ensuring that users must
                explicitly interact with the modal’s buttons or close icon.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3">
                        <Button color={ButtonColor.Primary} onClick={() => openModal('staticBackdrop')} style={{ width: 'fit-content' }}>Backdrop modal</Button>
                    </Stack>
                    <Modal show={isModalOpen('staticBackdrop')} onClose={closeModal} backdropClose={false}>
                        <Modal.Dialog>
                            <Modal.Header>
                                <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                <Button type="button" className="btn-close" onClick={closeModal}>&nbsp;</Button>
                            </Modal.Header>
                            <Modal.Body>
                                <Text el="p">This is the modal body content.</Text>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
                                <Button color={ButtonColor.Primary}>Save changes</Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ModalStaticBackdropCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Scrollable Modal
            </Heading>
            <Text className="mb-4">
                By default, the <Text el="strong">modal content</Text> is <Text el="strong">scrollable</Text>, allowing
                users to navigate through longer content without
                affecting the page behind it. This ensures that large forms or detailed information remain accessible
                within the modal view.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3">
                        <Button color={ButtonColor.Primary} onClick={() => openModal('scrollable')} style={{ width: 'fit-content' }}>Scrollable content</Button>
                    </Stack>
                    <Modal show={isModalOpen('scrollable')} onClose={closeModal}>
                        <Modal.Dialog>
                            <Modal.Header>
                                <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                <Button type="button" className="btn-close"
                                    onClick={closeModal}>&nbsp;</Button>
                            </Modal.Header>
                            <Modal.Body>
                                <Text el="p">This is the modal body content.</Text>
                                <Wrapper style={{ minHeight: '800px' }}>
                                    Light make signs our itself signs, whose. Herb, divided signs. Land herb won't so
                                    won't. Him sixth gathered fruitful whose. Air the Gathering day which itself all.
                                    Spirit fruitful shall of above multiply.
                                </Wrapper>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
                                <Button color={ButtonColor.Primary}>Save changes</Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ModalScrollableCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Independent Scrolling
            </Heading>
            <Text className="mb-4">
                When <Text el="strong">scrollable</Text> set to <Text el="strong">false</Text>, the modal becomes
                independent of the <Text el="strong">scroll</Text>. The background page remains
                fixed while only the <Text el="strong">modal content</Text> can <Text
                    el="strong">scroll</Text> internally.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3">
                        <Button color={ButtonColor.Primary} onClick={() => openModal('scrollIndependent')} style={{ width: 'fit-content' }}>Independent
                            scrollable content</Button>
                    </Stack>
                    <Modal show={isModalOpen('scrollIndependent')} onClose={closeModal}>
                        <Modal.Dialog scrollable={false}>
                            <Modal.Header>
                                <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                <Button type="button" className="btn-close"
                                    onClick={closeModal}>&nbsp;</Button>
                            </Modal.Header>
                            <Modal.Body>
                                <Text el="p">This is the modal body content.</Text>
                                <Wrapper style={{ minHeight: '800px' }}>
                                    Light make signs our itself signs, whose. Herb, divided signs. Land herb won't so
                                    won't. Him sixth gathered fruitful whose. Air the Gathering day which itself all.
                                    Spirit fruitful shall of above multiply.
                                </Wrapper>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button color={ButtonColor.Secondary}
                                    onClick={closeModal}>Close</Button>
                                <Button color={ButtonColor.Primary}>Save changes</Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ModalScrollableIndependentCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Vertically centered
            </Heading>

            <Text el="p">You can <Text el="strong">center</Text> the <Text el="strong">modal vertically</Text> in the
                viewport by setting the <Text el="strong">centered prop to true</Text>. This
                aligns the modal in the <Text el="strong">middle</Text> of the screen rather than starting from the top.</Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3">
                        <Button color={ButtonColor.Primary} onClick={() => openModal('centered')} style={{ width: 'fit-content' }}> Vertically centered</Button>
                    </Stack>
                    <Modal show={isModalOpen('centered')} onClose={closeModal}>
                        <Modal.Dialog centered={true}>
                            <Modal.Header>
                                <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                <Button type="button" className="btn-close"
                                    onClick={closeModal}>&nbsp;</Button>
                            </Modal.Header>
                            <Modal.Body>
                                <Text el="p">This is a vertically centered modal.</Text>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
                                <Button color={ButtonColor.Primary}>Save changes</Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ModalVerticallyCenteredCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Modal sizing
            </Heading>

            <Text el="p">You can control the <Text el="strong">size</Text> of the <Text el="strong">modal</Text> using
                the <Text el="strong">size prop</Text>. Different predefined sizes are
                available to suit various types of content such as small alerts, standard dialogs, or large detail
                views.</Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3" gap={Gap.Gap2}>
                        <StackItem>
                            <Button color={ButtonColor.Primary} onClick={() => openModal('xl')}>Extra large modal</Button>
                            <Modal show={isModalOpen('xl')} onClose={closeModal}>
                                <Modal.Dialog size={ModalSize.Xl}>
                                    <Modal.Header>
                                        <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                        <Button type="button" className="btn-close"
                                            onClick={closeModal}>&nbsp;</Button>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Text el="p">This is an extra large modal.</Text>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button color={ButtonColor.Secondary}
                                            onClick={closeModal}>Close</Button>
                                        <Button color={ButtonColor.Primary}>Save changes</Button>
                                    </Modal.Footer>
                                </Modal.Dialog>
                            </Modal>
                        </StackItem>
                        <StackItem>
                            <Button color={ButtonColor.Primary} onClick={() => openModal('lg')}>Large modal</Button>
                            <Modal show={isModalOpen('lg')} onClose={closeModal}>
                                <Modal.Dialog size={ModalSize.Lg}>
                                    <Modal.Header>
                                        <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                        <Button type="button" className="btn-close"
                                            onClick={closeModal}>&nbsp;</Button>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Text el="p">This is a large modal.</Text>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button color={ButtonColor.Secondary}
                                            onClick={closeModal}>Close</Button>
                                        <Button color={ButtonColor.Primary}>Save changes</Button>
                                    </Modal.Footer>
                                </Modal.Dialog>
                            </Modal>
                        </StackItem>
                        <StackItem>
                            <Button color={ButtonColor.Primary} onClick={() => openModal('md')}>Medium
                                modal</Button>
                            <Modal show={isModalOpen('md')} onClose={closeModal}>
                                <Modal.Dialog>
                                    <Modal.Header>
                                        <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                        <Button type="button" className="btn-close"
                                            onClick={closeModal}>&nbsp;</Button>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Text el="p">This is a medium size modal (default).</Text>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button color={ButtonColor.Secondary}
                                            onClick={closeModal}>Close</Button>
                                        <Button color={ButtonColor.Primary}>Save changes</Button>
                                    </Modal.Footer>
                                </Modal.Dialog>
                            </Modal>
                        </StackItem>
                        <StackItem>
                            <Button color={ButtonColor.Primary} onClick={() => openModal('sm')}>Small
                                modal</Button>
                            <Modal show={isModalOpen('sm')} onClose={closeModal}>
                                <Modal.Dialog size={ModalSize.Sm}>
                                    <Modal.Header>
                                        <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                        <Button type="button" className="btn-close"
                                            onClick={closeModal}>&nbsp;</Button>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Text el="p">This is a small size modal.</Text>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button color={ButtonColor.Secondary}
                                            onClick={closeModal}>Close</Button>
                                        <Button color={ButtonColor.Primary}>Save changes</Button>
                                    </Modal.Footer>
                                </Modal.Dialog>
                            </Modal>
                        </StackItem>
                    </Stack>

                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ModalSizingCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Full screen
            </Heading>
            <Text className="mb-4">
                By setting the <Text el="strong">fullscreen prop</Text> to true on <Text el="strong">Modal.Dialog</Text>,
                the modal expands to cover the entire viewport.
                This is useful for forms, dashboards, or content that requires maximum screen space.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3" gap={Gap.Gap2}>
                        <StackItem>
                            <Button color={ButtonColor.Primary} onClick={() => openModal('fullscreen')}>Full screen modal</Button>
                            <Modal show={isModalOpen('fullscreen')} onClose={closeModal} backdropClose={false}>
                                <Modal.Dialog fullscreen={ModalFullscreen.Always}>
                                    <Modal.Header>
                                        <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                        <Button type="button" className="btn-close"
                                            onClick={closeModal}>&nbsp;</Button>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Text el="p">This is the modal body content.</Text>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
                                        <Button color={ButtonColor.Primary}>Save changes</Button>
                                    </Modal.Footer>
                                </Modal.Dialog>
                            </Modal>
                        </StackItem>
                        <StackItem>
                            <Button color={ButtonColor.Primary} onClick={() => openModal('fullscreen-sm')}>Full screen below sm</Button>
                            <Modal show={isModalOpen('fullscreen-sm')} onClose={closeModal} backdropClose={false}>
                                <Modal.Dialog fullscreen={ModalFullscreen.SmDown}>
                                    <Modal.Header>
                                        <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                        <Button type="button" className="btn-close"
                                            onClick={closeModal}>&nbsp;</Button>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Text el="p">This is the modal body content.</Text>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
                                        <Button color={ButtonColor.Primary}>Save changes</Button>
                                    </Modal.Footer>
                                </Modal.Dialog>
                            </Modal>
                        </StackItem>
                        <StackItem>
                            <Button color={ButtonColor.Primary} onClick={() => openModal('fullscreen-md')}>Full screen below md</Button>
                            <Modal show={isModalOpen('fullscreen-md')} onClose={closeModal} backdropClose={false}>
                                <Modal.Dialog fullscreen={ModalFullscreen.MdDown}>
                                    <Modal.Header>
                                        <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                        <Button type="button" className="btn-close"
                                            onClick={closeModal}>&nbsp;</Button>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Text el="p">This is the modal body content.</Text>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
                                        <Button color={ButtonColor.Primary}>Save changes</Button>
                                    </Modal.Footer>
                                </Modal.Dialog>
                            </Modal>
                        </StackItem>
                        <StackItem>
                            <Button color={ButtonColor.Primary} onClick={() => openModal('fullscreen-lg')}>Full screen below lg</Button>
                            <Modal show={isModalOpen('fullscreen-lg')} onClose={closeModal} backdropClose={false}>
                                <Modal.Dialog fullscreen={ModalFullscreen.LgDown}>
                                    <Modal.Header>
                                        <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                        <Button type="button" className="btn-close"
                                            onClick={closeModal}>&nbsp;</Button>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Text el="p">This is the modal body content.</Text>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
                                        <Button color={ButtonColor.Primary}>Save changes</Button>
                                    </Modal.Footer>
                                </Modal.Dialog>
                            </Modal>
                        </StackItem>
                        <StackItem>
                            <Button color={ButtonColor.Primary} onClick={() => openModal('fullscreen-xl')}>Full screen below xl</Button>
                            <Modal show={isModalOpen('fullscreen-xl')} onClose={closeModal} backdropClose={false}>
                                <Modal.Dialog fullscreen={ModalFullscreen.XlDown}>
                                    <Modal.Header>
                                        <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                        <Button type="button" className="btn-close"
                                            onClick={closeModal}>&nbsp;</Button>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Text el="p">This is the modal body content.</Text>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
                                        <Button color={ButtonColor.Primary}>Save changes</Button>
                                    </Modal.Footer>
                                </Modal.Dialog>
                            </Modal>
                        </StackItem>
                        <StackItem>
                            <Button color={ButtonColor.Primary} onClick={() => openModal('fullscreen-xxl')}>Full screen below
                                xxl</Button>
                            <Modal show={isModalOpen('fullscreen-xxl')} onClose={closeModal} backdropClose={false}>
                                <Modal.Dialog fullscreen={ModalFullscreen.XxlDown}>
                                    <Modal.Header>
                                        <Heading el={'h5'} className="modal-title">Modal Title</Heading>
                                        <Button type="button" className="btn-close"
                                            onClick={closeModal}>&nbsp;</Button>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Text el="p">This is the modal body content.</Text>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
                                        <Button color={ButtonColor.Primary}>Save changes</Button>
                                    </Modal.Footer>
                                </Modal.Dialog>
                            </Modal>
                        </StackItem>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {FullScreenModalCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    )
}

export default ModalPage;


const ModalBasicCode = `<Button color={ButtonColor.Primary} onClick={() => openModal('basic')}>Launch demo modal</Button>
<Modal show={isModalOpen('basic')} onClose={closeModal}>
    <Modal.Dialog>
        <Modal.Header>
            <Heading el={'h5'} className="modal-title">Modal Title</Heading>
            <Button type="button" className="btn-close" onClick={closeModal}>&nbsp;</Button>
        </Modal.Header>
        <Modal.Body>
            <Text el="p">This is the modal body content.</Text>
        </Modal.Body>
        <Modal.Footer>
            <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
            <Button color={ButtonColor.Primary}>Save changes</Button>
        </Modal.Footer>
    </Modal.Dialog>
</Modal>`;

const ModalBasicCodeJs = `// Single state to manage many modals
const [activeModal, setActiveModal] = React.useState<string | null>(null);

// Generic handlers
const openModal = (modalName: string) => setActiveModal(modalName);
const closeModal = () => setActiveModal(null);

// Check if specific modal is open
const isModalOpen = (modalName: string) => activeModal === modalName;
`;

const ModalStaticBackdropCode = `<Button color={ButtonColor.Primary} onClick={() => openModal('staticBackdrop')}>Backdrop modal</Button>
<Modal show={isModalOpen('staticBackdrop')} onClose={closeModal} backdropClose={false}>
    <Modal.Dialog>
        <Modal.Header>
            <Heading el={'h5'} className="modal-title">Modal Title</Heading>
            <Button type="button" className="btn-close" onClick={closeModal}>&nbsp;</Button>
        </Modal.Header>
        <Modal.Body>
            <Text el="p">This is the modal body content.</Text>
        </Modal.Body>
        <Modal.Footer>
            <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
            <Button color={ButtonColor.Primary}>Save changes</Button>
        </Modal.Footer>
    </Modal.Dialog>
</Modal>
`;

const ModalScrollableCode = `<Button color={ButtonColor.Primary} onClick={showModalScrollHandler}>Scrollable content</Button>
<Modal show={modalScrollShow} onClose={hideModalScrollHandler}>
    <Modal.Dialog>
        <Modal.Header>
            <Heading el={'h5'} className="modal-title">Modal Title</Heading>
            <Button type="button" className="btn-close" onClick={hideModalScrollHandler}>&nbsp;</Button>
        </Modal.Header>
        <Modal.Body>
            <Text el="p">This is the modal body content.</Text>
            <Wrapper style={{minHeight: '800px'}}>
                Light make signs our itself signs, whose. Herb, divided signs. Land herb won't so
                won't. Him sixth gathered fruitful whose. Air the Gathering day which itself all.
                Spirit fruitful shall of above multiply.
            </Wrapper>
        </Modal.Body>
        <Modal.Footer>
            <Button color={ButtonColor.Secondary} onClick={hideModalScrollHandler}>Close</Button>
            <Button color={ButtonColor.Primary}>Save changes</Button>
        </Modal.Footer>
    </Modal.Dialog>
</Modal>
`;

const ModalScrollableIndependentCode = `<Button color={ButtonColor.Primary} onClick={showModalScrollIndependentHandler}>Independent scrollable content</Button>
<Modal show={modalScrollIndependentShow} onClose={hideModalScrollIndependentHandler}>
    <Modal.Dialog scrollable={false}>
        <Modal.Header>
            <Heading el={'h5'} className="modal-title">Modal Title</Heading>
            <Button type="button" className="btn-close" onClick={hideModalScrollIndependentHandler}>&nbsp;</Button>
        </Modal.Header>
        <Modal.Body>
            <Text el="p">This is the modal body content.</Text>
            <Wrapper style={{minHeight: '800px'}}>
                Light make signs our itself signs, whose. Herb, divided signs. Land herb won't so
                won't. Him sixth gathered fruitful whose. Air the Gathering day which itself all.
                Spirit fruitful shall of above multiply.
            </Wrapper>
        </Modal.Body>
        <Modal.Footer>
            <Button color={ButtonColor.Secondary} onClick={hideModalScrollIndependentHandler}>Close</Button>
            <Button color={ButtonColor.Primary}>Save changes</Button>
        </Modal.Footer>
    </Modal.Dialog>
</Modal>
`;

const ModalVerticallyCenteredCode = `<Button color={ButtonColor.Primary} onClick={showModalCenterHandler}> Vertically centered</Button>
<Modal show={modalCenterShow} onClose={hideCenterModalHandler}>
    <Modal.Dialog centered={true}>
        <Modal.Header>
            <Heading el={'h5'} className="modal-title">Modal Title</Heading>
            <Button type="button" className="btn-close" onClick={hideCenterModalHandler}>&nbsp;</Button>
        </Modal.Header>
        <Modal.Body>
            <Text el="p">This is a vertically centered modal.</Text>
        </Modal.Body>
        <Modal.Footer>
            <Button color={ButtonColor.Secondary} onClick={hideCenterModalHandler}>Close</Button>
            <Button color={ButtonColor.Primary}>Save changes</Button>
        </Modal.Footer>
    </Modal.Dialog>
</Modal>
`;

const ModalSizingCode = `<Modal show={modalExtraLargeShow} onClose={hideExtraLargeModalHandler}>
<Modal.Dialog size={ModalSize.Xl}>
        ...
    </Modal.Dialog>
</Modal>

<Modal show={modalLargeShow} onClose={hideLargeModalHandler}>
    <Modal.Dialog size={ModalSize.Lg}>
        ...
    </Modal.Dialog>
</Modal>

<Modal show={modalMediumShow} onClose={hideMediumModalHandler}>
    <Modal.Dialog>
        ...
    </Modal.Dialog>
</Modal>

<Modal show={modalSmallShow} onClose={hideSmallModalHandler}>
    <Modal.Dialog size={ModalSize.Sm}>
        ...
    </Modal.Dialog>
</Modal>
`;

const FullScreenModalCode = `<Modal show={modalAlwaysShow} onClose={hideAlwaysModalHandler} backdropClose={false}>
    <Modal.Dialog fullscreen={ModalFullscreen.Always}>
       ...
    </Modal.Dialog>
</Modal>

<Modal show={modalAlwaysShow} onClose={hideAlwaysModalHandler} backdropClose={false}>
    <Modal.Dialog fullscreen={ModalFullscreen.SmDown}>
       ...
    </Modal.Dialog>
</Modal>

<Modal show={modalAlwaysShow} onClose={hideAlwaysModalHandler} backdropClose={false}>
    <Modal.Dialog fullscreen={ModalFullscreen.MdDown}>
       ...
    </Modal.Dialog>
</Modal>

<Modal show={modalAlwaysShow} onClose={hideAlwaysModalHandler} backdropClose={false}>
    <Modal.Dialog fullscreen={ModalFullscreen.LgDown}>
       ...
    </Modal.Dialog>
</Modal>

<Modal show={modalAlwaysShow} onClose={hideAlwaysModalHandler} backdropClose={false}>
    <Modal.Dialog fullscreen={ModalFullscreen.XlDown}>
       ...
    </Modal.Dialog>
</Modal>

<Modal show={modalAlwaysShow} onClose={hideAlwaysModalHandler} backdropClose={false}>
    <Modal.Dialog fullscreen={ModalFullscreen.XxlDown}>
       ...
    </Modal.Dialog>
</Modal>
`;



